/**
 * RecommendationDialog Component
 * Dialog showing single recommendation details (85% viewport, 15% visible around edges)
 */

import { Minus, Plus } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import type { Recommendation } from '../../../types/agenticChat';
import type { Language } from './VoiceOrderingContainer';
import { formatPrice, getArabicName, getItemName } from './recommendationHelpers';

interface RecommendationDialogProps {
  item: Recommendation | null;
  isOpen: boolean;
  onClose: () => void;
  onAddToOrder: (item: Recommendation, quantity: number) => void;
  language: Language;
}

export function RecommendationDialog({
  item,
  isOpen,
  onClose,
  onAddToOrder,
  language,
}: RecommendationDialogProps) {
  const [quantity, setQuantity] = useState(1);
  const [isDragging, setIsDragging] = useState(false);
  const [startY, setStartY] = useState(0);
  const [dragDistance, setDragDistance] = useState(0);
  const dragHandleRef = useRef<HTMLDivElement>(null);
  const sheetRef = useRef<HTMLDivElement>(null);

  // Reset quantity when dialog opens or item changes
  useEffect(() => {
    if (isOpen) {
      setQuantity(1);
      setIsDragging(false);
      setDragDistance(0);
    }
  }, [isOpen, item]);

  // Prevent body scroll when dialog is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = '';
      };
    }
  }, [isOpen]);

  if (!isOpen || !item) {
    return null;
  }

  const itemName = getItemName(item.item, language);
  const itemNameAr = getArabicName(item.item);
  const displayName = language === 'ar' && itemNameAr ? itemNameAr : itemName;
  const totalPrice = item.price * quantity;

  const handleDecreaseQuantity = () => {
    if (quantity > 1) {
      setQuantity(quantity - 1);
    }
  };

  const handleIncreaseQuantity = () => {
    setQuantity(quantity + 1);
  };

  const handleAddToOrder = () => {
    onAddToOrder(item, quantity);
  };

  // Touch event handlers for swipe-down-to-close
  const handleTouchStart = (e: React.TouchEvent) => {
    if (!isOpen) return;
    const touch = e.touches[0];
    setStartY(touch.clientY);
    setIsDragging(true);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDragging || !isOpen) return;
    const touch = e.touches[0];
    const deltaY = touch.clientY - startY;
    
    // Only allow downward dragging
    if (deltaY > 0) {
      setDragDistance(deltaY);
    }
  };

  const handleTouchEnd = () => {
    if (!isDragging) return;
    setIsDragging(false);

    // Close if dragged down more than 100px
    const threshold = 100;
    if (dragDistance > threshold) {
      onClose();
    } else {
      // Snap back
      setDragDistance(0);
    }
  };

  // Mouse event handlers for desktop drag support
  const handleMouseDown = (e: React.MouseEvent) => {
    if (!isOpen) return;
    setStartY(e.clientY);
    setIsDragging(true);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging || !isOpen) return;
    const deltaY = e.clientY - startY;
    
    if (deltaY > 0) {
      setDragDistance(deltaY);
    }
  };

  const handleMouseUp = () => {
    handleTouchEnd();
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-end justify-center"
      style={{
        background: 'rgba(0, 0, 0, 0.5)',
        backdropFilter: 'blur(8px)',
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      {/* Bottom Sheet Container */}
      <div
        ref={sheetRef}
        className="relative flex flex-col overflow-hidden rounded-t-3xl"
        style={{
          width: '100%',
          maxHeight: '90vh',
          height: 'auto',
          background: 'var(--card-bg)',
          animation: !isDragging ? 'slideUpFromBottom 300ms ease-out' : 'none',
          transform: `translateY(${isDragging ? dragDistance : 0}px)`,
          transition: isDragging ? 'none' : 'transform 200ms ease-out',
          borderTopLeftRadius: '1.5rem',
          borderTopRightRadius: '1.5rem',
          borderBottomLeftRadius: '0',
          borderBottomRightRadius: '0',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Drag Handle Indicator */}
        <div
          ref={dragHandleRef}
          className="flex justify-center pt-3 pb-2 cursor-grab active:cursor-grabbing"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
        >
          <div
            style={{
              width: '40px',
              height: '4px',
              borderRadius: '2px',
              background: 'var(--border)',
            }}
          />
        </div>
        {/* Content Container - Scrollable */}
        <div
          className="flex flex-col overflow-y-auto"
          style={{
            direction: language === 'ar' ? 'rtl' : 'ltr',
            maxHeight: 'calc(90vh - 60px)', // Account for drag handle and padding
          }}
        >
          {/* Image Section */}
          <div
            className="relative flex flex-shrink-0 items-center justify-center overflow-hidden"
            style={{
              width: '100%',
              height: '250px',
              maxHeight: '40%',
              background: 'var(--cream-light)',
            }}
          >
            {item.img_path ? (
              <img
                src={item.img_path}
                alt={itemName}
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                  const parent = e.currentTarget.parentElement;
                  if (parent) {
                    parent.innerHTML = '<div style="display: flex; align-items: center; justify-content: center; height: 100%; width: 100%; font-size: 4rem; background: var(--cream-medium);">🍽️</div>';
                  }
                }}
              />
            ) : (
              <div
                className="flex h-full w-full items-center justify-center text-7xl"
                style={{ background: 'var(--cream-medium)' }}
              >
                🍽️
              </div>
            )}
          </div>

          {/* Content Section */}
          <div
            className="flex flex-1 flex-col justify-between px-5 py-4"
            style={{
              background: 'var(--card-bg)',
              color: 'var(--text-primary)',
            }}
          >
            {/* Item Details */}
            <div>
              <h2
                className="mb-2 text-2xl font-bold"
                style={{ color: 'var(--text-primary)' }}
              >
                {displayName}
              </h2>

              <p
                className="mb-2 text-sm leading-normal"
                style={{ color: 'var(--text-secondary)' }}
              >
                {language === 'en'
                  ? 'A delicious menu item prepared fresh to order. Perfect for any meal!'
                  : 'عنصر قائمة لذيذ يتم تحضيره طازجًا حسب الطلب. مثالي لأي وجبة!'}
              </p>

              {/* Price */}
              <div className="mt-3">
                <p
                  className="text-2xl font-bold"
                  style={{ color: 'var(--accent-gold)' }}
                >
                  {formatPrice(item.price, language)}
                </p>
              </div>
            </div>

            {/* Quantity Selector and Add Button */}
            <div className="space-y-3">
              {/* Quantity Selector */}
              <div className="flex items-center justify-between">
                <span
                  className="text-sm font-medium"
                  style={{ color: 'var(--text-secondary)' }}
                >
                  {language === 'en' ? 'Quantity' : 'الكمية'}
                </span>
                <div className="flex items-center gap-3">
                  <button
                    onClick={handleDecreaseQuantity}
                    disabled={quantity <= 1}
                    className="flex h-10 w-10 items-center justify-center rounded-full transition-all disabled:opacity-50 disabled:cursor-not-allowed hover:bg-opacity-80"
                    style={{
                      background: 'var(--accent-gold)',
                      color: 'white',
                    }}
                    aria-label={language === 'en' ? 'Decrease quantity' : 'تقليل الكمية'}
                  >
                    <Minus className="h-5 w-5" />
                  </button>
                  <span
                    className="w-12 text-center text-2xl font-bold"
                    style={{ color: 'var(--text-primary)' }}
                  >
                    {quantity}
                  </span>
                  <button
                    onClick={handleIncreaseQuantity}
                    className="flex h-10 w-10 items-center justify-center rounded-full transition-all hover:bg-opacity-80"
                    style={{
                      background: 'var(--accent-gold)',
                      color: 'white',
                    }}
                    aria-label={language === 'en' ? 'Increase quantity' : 'زيادة الكمية'}
                  >
                    <Plus className="h-5 w-5" />
                  </button>
                </div>
              </div>

              {/* Add to Order Button */}
              <button
                onClick={handleAddToOrder}
                className="w-full rounded-lg py-3 text-lg font-semibold transition-all hover:bg-opacity-90 active:scale-95"
                style={{
                  background: 'var(--accent-gold)',
                  color: 'white',
                }}
              >
                {language === 'en'
                  ? `Add to Order • ${formatPrice(totalPrice, language)}`
                  : `أضف إلى الطلب • ${formatPrice(totalPrice, language)}`}
              </button>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes slideUpFromBottom {
          from {
            opacity: 0;
            transform: translateY(100%);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
}

